import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadSalesHistoryProductDetailUrl,
  zadSalesHistoryProductsUrl,
  zadSalesHistorySalesUrl,
  zadSalesHistorySummaryUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {buildZadSalesHistoryQueryString} from '../zad/salesHistoryHelpers'
import {
  ZadSalesHistoryProductDetail,
  ZadSalesHistoryProductsPage,
  ZadSalesHistorySalesPage,
  ZadSalesHistorySummary,
} from '../zad/salesHistoryTypes'
import {
  ZadSalesHistoryProductDetailRequest,
  ZadSalesHistoryRequest,
} from './types'

function unwrapReport<T>(payload: unknown): T {
  if (!payload || typeof payload !== 'object') return {} as T
  const body = payload as Record<string, unknown>
  if (body.data && typeof body.data === 'object') return body.data as T
  return payload as T
}

function flattenProductDetail(payload: unknown): ZadSalesHistoryProductDetail {
  const data = unwrapReport<ZadSalesHistoryProductDetail & {summary?: Partial<ZadSalesHistoryProductDetail>}>(
    payload
  )
  const nested: Partial<ZadSalesHistoryProductDetail> =
    data.summary && typeof data.summary === 'object' ? data.summary : {}
  return {
    ...nested,
    ...data,
    productId: String(data.productId || nested.productId || ''),
    quantitySold: Number(data.quantitySold ?? nested.quantitySold ?? 0),
    quantityReturned: Number(data.quantityReturned ?? nested.quantityReturned ?? 0),
    grossSales: Number(data.grossSales ?? nested.grossSales ?? 0),
    totalDiscount: Number(data.totalDiscount ?? nested.totalDiscount ?? 0),
    netSales: Number(data.netSales ?? nested.netSales ?? 0),
    returnValue: Number(data.returnValue ?? nested.returnValue ?? 0),
    netCommercialValue: Number(data.netCommercialValue ?? nested.netCommercialValue ?? 0),
    occurrences: data.occurrences || [],
    returns: data.returns || [],
    daily: data.daily || [],
  }
}

function summaryQuery(query: ZadSalesHistoryRequest['query']) {
  return buildZadSalesHistoryQueryString({
    period: query.period,
    customerKind: query.customerKind,
    discount: query.discount,
  })
}

function productsQuery(query: ZadSalesHistoryRequest['query']) {
  return buildZadSalesHistoryQueryString({
    period: query.period,
    customerKind: query.customerKind,
    discount: query.discount,
    pageNumber: query.productPageNumber,
    pageCount: query.productPageCount,
    sortField: query.productSortField,
    sortOrder: query.productSortOrder,
  })
}

function salesQuery(query: ZadSalesHistoryRequest['query']) {
  return buildZadSalesHistoryQueryString({
    period: query.period,
    customerKind: query.customerKind,
    discount: query.discount,
    pageNumber: query.salesPageNumber,
    pageCount: query.salesPageCount,
    sortField: query.salesSortField,
    sortOrder: query.salesSortOrder,
  })
}

export const fetchZadSalesHistorySummary = createAsyncThunk(
  'zadSalesHistory/summary',
  async (payload: ZadSalesHistoryRequest, {rejectWithValue}) => {
    try {
      const qs = summaryQuery(payload.query)
      const res = await axios.get(`${zadSalesHistorySummaryUrl}?${qs}`)
      return {
        requestKey: payload.requestKey,
        report: unwrapReport<ZadSalesHistorySummary>(res.data),
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSalesHistoryProducts = createAsyncThunk(
  'zadSalesHistory/products',
  async (payload: ZadSalesHistoryRequest, {rejectWithValue}) => {
    try {
      const qs = productsQuery(payload.query)
      const res = await axios.get(`${zadSalesHistoryProductsUrl}?${qs}`)
      return {
        requestKey: payload.requestKey,
        report: unwrapReport<ZadSalesHistoryProductsPage>(res.data),
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSalesHistorySales = createAsyncThunk(
  'zadSalesHistory/sales',
  async (payload: ZadSalesHistoryRequest, {rejectWithValue}) => {
    try {
      const qs = salesQuery(payload.query)
      const res = await axios.get(`${zadSalesHistorySalesUrl}?${qs}`)
      return {
        requestKey: payload.requestKey,
        report: unwrapReport<ZadSalesHistorySalesPage>(res.data),
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSalesHistoryProductDetail = createAsyncThunk(
  'zadSalesHistory/productDetail',
  async (payload: ZadSalesHistoryProductDetailRequest, {rejectWithValue}) => {
    try {
      const qs = summaryQuery(payload.query)
      const res = await axios.get(`${zadSalesHistoryProductDetailUrl(payload.productId)}?${qs}`)
      return {
        requestKey: payload.requestKey,
        productId: payload.productId,
        report: flattenProductDetail(res.data),
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
